import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../store';
import { 
  Package, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  X,
  Upload,
  Image,
  Barcode,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  FolderTree,
} from 'lucide-react';
import { toast } from 'sonner';
import AdminCategoriesTab from './AdminCategoriesTab';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProductsPage() {
  const { token } = useAuthStore();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);

  const [form, setForm] = useState({
    barcode: '',
    name: '',
    price: '',
    stock: '',
    description: '',
    image_url: '',
    author: '',
    publisher: '',
    isbn: '',
    category: '',
    kdv_orani: ''
  });

  useEffect(() => {
    fetchProducts();
  }, [search]);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/categories`, { headers: { Authorization: `Bearer ${token}` } });
        setCategories(res.data || []);
      } catch {}
    };
    fetchCats();
  }, [token, activeTab]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '100');
      
      const res = await axios.get(`${API_URL}/api/products?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setForm({
      barcode: '',
      name: '',
      price: '',
      stock: '',
      description: '',
      image_url: '',
      author: '',
      publisher: '',
      isbn: '',
      category: '',
      kdv_orani: ''
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setForm({
      barcode: product.barcode || '',
      name: product.name || '',
      price: product.price?.toString() || '',
      stock: product.stock?.toString() || '',
      description: product.description || '',
      image_url: product.image_url || '',
      author: product.author || '',
      publisher: product.publisher || '',
      isbn: product.isbn || '',
      category: product.category || '',
      kdv_orani: product.kdv_orani?.toString() || '0'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.barcode || !form.name || !form.price) {
      toast.error('Barkod, isim ve fiyat zorunludur');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock) || 0,
        kdv_orani: parseFloat(form.kdv_orani) || 0
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ürün güncellendi');
      } else {
        await axios.post(`${API_URL}/api/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ürün eklendi');
      }

      setShowModal(false);
      fetchProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error(err.response?.data?.detail || 'Ürün kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`"${product.name}" ürününü silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setDeleting(product.id);
      await axios.delete(`${API_URL}/api/products/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ürün silindi');
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      toast.error('Ürün silinemedi');
    } finally {
      setDeleting(null);
    }
  };

  const toggleProductActive = async (product) => {
    try {
      const res = await axios.put(`${API_URL}/api/products/${product.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchProducts();
    } catch {
      toast.error('Durum degistirilemedi');
    }
  };


  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // For now, we'll use a placeholder - in production, upload to cloud storage
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm({ ...form, image_url: reader.result });
    };
    reader.readAsDataURL(file);
    toast.info('Görsel yüklendi (geçici)');
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    toast.info('Excel import özelliği Wolvox entegrasyonu ile birlikte gelecek');
    e.target.value = '';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const productsCategories = [...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Ürün Yönetimi</h1>
          <p className="text-sm text-gray-500">Ürünleri ve kategorileri yönetin</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'products' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-products"
        >
          <Package className="w-4 h-4" />Ürünler
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'categories' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-categories"
        >
          <FolderTree className="w-4 h-4" />Kategoriler
        </button>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && <AdminCategoriesTab />}

      {/* Products Tab */}
      {activeTab === 'products' && (
      <>
      {/* Product Actions */}
      <div className="flex items-center gap-2 justify-end mb-2">
        <button
          onClick={() => excelInputRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          <FileSpreadsheet className="w-4 h-4" />
          <span className="hidden sm:inline">Excel İçe Aktar</span>
        </button>
        <input ref={excelInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelImport} className="hidden" />
        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-3 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a]"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" />Yeni Ürün
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Ürün adı, barkod veya yazar ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500 mb-4">
              {search ? 'Arama kriterlerinize uygun ürün yok.' : 'Henüz ürün eklenmemiş.'}
            </p>
            <button
              onClick={openAddModal}
              className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              İlk Ürünü Ekle
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-gray-500">#</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">Görsel</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500">Ürün</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 hidden md:table-cell">Barkod</th>
                  <th className="text-center px-6 py-2.5 text-xs font-semibold text-gray-500 w-32">Fiyat</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">KDV</th>
                  <th className="text-center px-6 py-2.5 text-xs font-semibold text-gray-500 w-24">Stok</th>
                  <th className="text-center px-6 py-2.5 text-xs font-semibold text-gray-500 hidden lg:table-cell">Kategori</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">Satış</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => (
                  <tr key={product.id} className={`border-b border-gray-100 group transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50 align-middle`} data-testid={`product-row-${product.barcode}`}>
                    <td className="px-2 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-10 h-14 object-cover rounded shrink-0" />
                        ) : (
                          <div className="w-10 h-14 bg-gray-100 rounded flex items-center justify-center shrink-0">
                            <Package className="w-5 h-5 text-gray-300" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <p className="font-medium text-gray-900">{product.name}</p>
                      {product.author && (
                        <p className="text-[11px] text-gray-400">{product.author}</p>
                      )}
                      <div className="flex items-center justify-center gap-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditModal(product)} className="text-[11px] text-gray-500 hover:text-gray-800 font-medium" data-testid={`edit-${product.barcode}`}>Duzenle</button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleDelete(product)} disabled={deleting === product.id} className="text-[11px] text-red-400 hover:text-[#1a224f] font-medium disabled:opacity-50" data-testid={`delete-${product.barcode}`}>
                          {deleting === product.id ? 'Siliniyor...' : 'Sil'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center hidden md:table-cell">
                      <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{product.barcode}</code>
                    </td>
                    <td className="px-6 py-2 text-center">
                      <span className="font-semibold text-gray-900 text-xs">{formatCurrency(product.price)}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {product.kdv_orani > 0 ? (
                        <span className="text-xs text-gray-600 font-medium">%{product.kdv_orani}</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-2 text-center">
                      <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        product.stock > 10 ? 'bg-green-100 text-green-700' :
                        product.stock > 0 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-6 py-2 text-center hidden lg:table-cell">
                      {product.category ? (
                        <span className="text-xs text-gray-600">{product.category}</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Switch
                        checked={product.is_active !== false}
                        onCheckedChange={() => toggleProductActive(product)}
                        data-testid={`toggle-active-${product.barcode}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Count */}
      <div className="text-sm text-gray-500 text-center">
        Toplam {products.length} ürün
      </div>

      {/* Add/Edit Modal - Mobilde tam ekran */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/50">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-xl flex flex-col h-full sm:h-auto sm:max-h-[90vh]">
            {/* Sticky Modal Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 sm:py-4 flex items-center justify-between shrink-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
                aria-label="Kapat"
                data-testid="close-product-modal"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4" id="product-form">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ürün Görseli</label>
                <div className="flex items-start gap-4">
                  {form.image_url ? (
                    <div className="relative">
                      <img
                        src={form.image_url}
                        alt="Preview"
                        className="w-24 h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: '' })}
                        className="absolute -top-2 -right-2 p-1 bg-[#1a224f] text-white rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-[#5b67a0] hover:text-red-500 transition-colors"
                    >
                      <Upload className="w-6 h-6 mb-1" />
                      <span className="text-xs">Yükle</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="veya görsel URL'si girin"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    />
                    <p className="text-xs text-gray-500 mt-1">Görsel URL'si veya dosya yükleyebilirsiniz</p>
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Barkod <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={form.barcode}
                      onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                      placeholder="9786051234567"
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                      required
                      data-testid="barcode-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                  <input
                    type="text"
                    value={form.isbn}
                    onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                    placeholder="978-605-1234-56-7"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ürün Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ürün adını girin"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yazar</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    placeholder="Yazar adı"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yayınevi</label>
                  <input
                    type="text"
                    value={form.publisher}
                    onChange={(e) => setForm({ ...form, publisher: e.target.value })}
                    placeholder="Yayınevi"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fiyat (TL) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    required
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stok</label>
                  <input
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    data-testid="stock-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">KDV Orani (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.kdv_orani}
                    onChange={(e) => setForm({ ...form, kdv_orani: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    data-testid="kdv-orani-product-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                  <Select value={form.category || '_none_'} onValueChange={(val) => setForm({ ...form, category: val === '_none_' ? '' : val })} data-testid="product-category-select">
                    <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm bg-white" data-testid="product-category-select">
                      <SelectValue placeholder="Kategori Seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Kategori Seçin...</SelectItem>
                      {categories.filter(c => !c.parent_id).map(cat => {
                        const subs = categories.filter(s => s.parent_id === cat.id);
                        return (
                          <React.Fragment key={cat.id}>
                            <SelectItem value={cat.name}>{cat.name}</SelectItem>
                            {subs.map(sub => (
                              <SelectItem key={sub.id} value={sub.name}>&nbsp;&nbsp;└─ {sub.name}</SelectItem>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description - HTML Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama (HTML destekli)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="<p>Ürün açıklaması...</p>"
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="description-input"
                />
                <p className="text-xs text-gray-500 mt-1">HTML etiketleri kullanabilirsiniz: &lt;p&gt;, &lt;b&gt;, &lt;ul&gt;, &lt;li&gt; vb.</p>
              </div>

              {/* Preview */}
              {form.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Önizleme</label>
                  <div 
                    className="p-3 bg-gray-50 rounded-lg prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: form.description }}
                  />
                </div>
              )}

              {/* Form content ends, action footer is sticky outside */}
            </form>

            {/* Sticky Action Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-2 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 sm:flex-initial px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                data-testid="cancel-product-btn"
              >
                İptal
              </button>
              <button
                type="submit"
                form="product-form"
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</>
                ) : (
                  <><Save className="w-4 h-4" />{editingProduct ? 'Güncelle' : 'Kaydet'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
